import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useApiCacheRefresh } from '../lib/apiCache';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { formatDatePL } from '../components/DatePickerField';
import { pl } from 'date-fns/locale';
import { 
  GripVertical, Calendar, User, DollarSign, TrendingUp, 
  Clock, Sparkles, LayoutGrid, Archive, Lock, Truck
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// New status configuration with colors
const statusConfig = {
  'lead': { 
    label: 'Lead/do pomiaru', 
    color: '#0000FF',
    bgClass: 'bg-blue-600',
    bgColor: 'bg-blue-600/10',
    borderColor: 'border-blue-600'
  },
  'akceptacja': { 
    label: 'Akceptacja', 
    color: '#FFFF00',
    bgClass: 'bg-yellow-400',
    bgColor: 'bg-yellow-400/10',
    borderColor: 'border-yellow-400'
  },
  'brak_akceptacji': { 
    label: 'Brak akceptacji', 
    color: '#696969',
    bgClass: 'bg-gray-500',
    bgColor: 'bg-gray-500/10',
    borderColor: 'border-gray-500',
    archived: true
  },
  'oplacone': { 
    label: 'Opłacone', 
    color: '#2E8B57',
    bgClass: 'bg-emerald-600',
    bgColor: 'bg-emerald-600/10',
    borderColor: 'border-emerald-600'
  },
  'dostawa': { 
    label: 'Dostawa', 
    color: '#800080',
    bgClass: 'bg-purple-600',
    bgColor: 'bg-purple-600/10',
    borderColor: 'border-purple-600'
  },
  'w_realizacji': { 
    label: 'W realizacji', 
    color: '#FFA500',
    bgClass: 'bg-orange-500',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500'
  },
  'zakonczone': { 
    label: 'Zakończono', 
    color: '#006400',
    bgClass: 'bg-green-700',
    bgColor: 'bg-green-700/10',
    borderColor: 'border-green-700'
  },
  // Legacy support
  'do_realizacji': { 
    label: 'Do realizacji', 
    color: '#0000FF',
    bgClass: 'bg-blue-600',
    bgColor: 'bg-blue-600/10',
    borderColor: 'border-blue-600'
  },
  'w_toku': { 
    label: 'W toku', 
    color: '#FFA500',
    bgClass: 'bg-orange-500',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500'
  },
  'zakończony': { 
    label: 'Zakończony', 
    color: '#006400',
    bgClass: 'bg-green-700',
    bgColor: 'bg-green-700/10',
    borderColor: 'border-green-700'
  }
};

// Statuses to show on Kanban board (leads, waiting and rejected are in /leads now)
const kanbanStatuses = ['akceptacja', 'oplacone', 'dostawa', 'w_realizacji', 'zakonczone'];

// Statusy widoczne dla roli Montaż (RBAC - tylko 5, 6, 7)
const MONTAZ_VISIBLE_STATUSES = ['dostawa', 'w_realizacji', 'zakonczone'];

// Helper: delivery date badge color
const getDeliveryDateStyle = (deliveryDate, status) => {
  if (!deliveryDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const delivery = new Date(deliveryDate);
  delivery.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((delivery - today) / (1000 * 60 * 60 * 24));

  // Overdue and not yet moved to "W realizacji" or later
  if (diffDays < 0 && status !== 'w_realizacji' && status !== 'zakonczone') {
    return { bg: 'bg-red-500/20', border: 'border-red-500/50', text: 'text-red-300', label: 'Opóźnienie' };
  }
  // Today or within 2 days
  if (diffDays >= 0 && diffDays <= 2) {
    return { bg: 'bg-orange-500/20', border: 'border-orange-500/50', text: 'text-orange-300', label: diffDays === 0 ? 'Dzisiaj' : `Za ${diffDays} dn.` };
  }
  return { bg: 'bg-purple-500/15', border: 'border-purple-500/30', text: 'text-purple-300', label: null };
};

const KanbanCard = ({ project, onDragStart, onDragEnd }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const showFinancials = user?.role !== 'montaz' && user?.role !== 'guest';

  const handleTitleClick = (e) => {
    e.stopPropagation();
    navigate(`/projects/${project.id}`);
  };

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, project)}
      onDragEnd={onDragEnd}
      className="bg-[var(--crm-bg-main)] border border-[var(--crm-border)] rounded-lg p-3 cursor-grab active:cursor-grabbing hover:border-[#FF6B35]/60 transition-all group"
      data-testid={`kanban-card-${project.id}`}
    >
      <div className="flex items-start gap-2 mb-2">
        <GripVertical className="w-4 h-4 text-[var(--crm-text-muted)] opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-0.5" />
        <h3
          onClick={handleTitleClick}
          className="text-[var(--crm-text-primary)] font-medium text-sm line-clamp-2 cursor-pointer hover:text-[#FF6B35] hover:underline underline-offset-2 transition-colors flex-1"
          data-testid={`kanban-card-title-${project.id}`}
        >
          {project.nazwa}
        </h3>
        {project.owner_initials && (
          <span className="bg-[var(--crm-bg-btn-secondary)] text-white px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0">
            {project.owner_initials}
          </span>
        )}
      </div>
      
      <p className="text-[var(--crm-text-secondary)] text-xs mb-2 flex items-center gap-1">
        <User className="w-3 h-3 shrink-0" />
        <span className="truncate">{project.klient}</span>
      </p>
      
      {project.tags?.includes('Zaniedbany') && (
        <span className="inline-block bg-red-500/20 text-red-400 border border-red-500/30 text-[10px] font-semibold px-1.5 py-0.5 rounded mb-2" data-testid={`kanban-neglected-${project.id}`}>
          Zaniedbany
        </span>
      )}
      
      {showFinancials && !project._masked && (
        <div className="flex items-center justify-between text-xs mb-2">
          <span className="text-[var(--crm-text-muted)] flex items-center gap-1">
            <DollarSign className="w-3 h-3" />
            {project.total_offer?.toLocaleString('pl-PL')} zł
          </span>
          <span className={`font-medium ${(project.net_margin_percent || project.margin_percent) >= 20 ? 'text-emerald-400' : 'text-amber-400'}`}>
            {(project.net_margin_percent || project.margin_percent)?.toFixed(1)}%
          </span>
        </div>
      )}
      
      <div className="flex items-center gap-2 text-[11px] text-[var(--crm-text-muted)]">
        <Calendar className="w-3 h-3 shrink-0" />
        {new Date(project.created_at).toLocaleDateString('pl-PL')}
      </div>
      
      {/* Delivery Date badge */}
      {project.delivery_date && (
        (() => {
          const style = getDeliveryDateStyle(project.delivery_date, project.status);
          return style ? (
            <div className={`flex items-center gap-1.5 mt-2 px-2 py-1 rounded-md border ${style.bg} ${style.border}`}>
              <Truck className={`w-3 h-3 ${style.text}`} />
              <span className={`text-xs font-medium ${style.text}`}>
                {new Date(project.delivery_date).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' })}
              </span>
              {style.label && <span className={`text-[10px] ${style.text} opacity-75`}>({style.label})</span>}
            </div>
          ) : null;
        })()
      )}

      {/* AI Analysis badge */}
      {project.analiza_ai && project.status === 'zakonczone' && (
        <div className="mt-2 pt-2 border-t border-[var(--crm-border)]">
          <div className="flex items-start gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-purple-400 shrink-0 mt-0.5" />
            <p className="text-[11px] text-purple-300 line-clamp-2">{project.analiza_ai}</p>
          </div>
        </div>
      )}
      
      {/* Completed date */}
      {project.completed_at && (
        <div className="mt-1.5 flex items-center gap-1 text-[11px] text-emerald-400">
          <Clock className="w-3 h-3" />
          {new Date(project.completed_at).toLocaleDateString('pl-PL')}
        </div>
      )}
    </div>
  );
};

const KanbanColumn = ({ status, projects, onDrop, onDragOver, onCardDragStart, onCardDragEnd, isDragOver, allowedStatuses }) => {
  const config = statusConfig[status] || { label: status, bgClass: 'bg-slate-500', borderColor: 'border-slate-500' };
  const canDrop = allowedStatuses?.includes(status);
  
  return (
    <div 
      className={`flex-1 min-w-[200px] max-w-[280px] bg-[var(--crm-bg-panel)] rounded-xl p-3 ${isDragOver && canDrop ? 'ring-2 ring-[#FF6B35]' : ''} ${!canDrop ? 'opacity-60' : ''}`}
      onDrop={(e) => canDrop && onDrop(e, status)}
      onDragOver={canDrop ? onDragOver : undefined}
      data-testid={`kanban-column-${status}`}
    >
      <div className={`flex items-center gap-2 mb-3 pb-2 border-b ${config.borderColor}`}>
        <div 
          className="w-3 h-3 rounded-full" 
          style={{ backgroundColor: config.color }}
        />
        <h2 className="text-[var(--crm-text-primary)] font-semibold text-sm">{config.label}</h2>
        <Badge 
          className="ml-auto text-[var(--crm-text-secondary)] text-xs"
          style={{ backgroundColor: config.color }}
        >
          {projects.length}
        </Badge>
      </div>
      
      <div className="space-y-2 min-h-[150px] max-h-[60vh] overflow-y-auto">
        {projects.map(project => (
          <KanbanCard
            key={project.id}
            project={project}
            onDragStart={onCardDragStart}
            onDragEnd={onCardDragEnd}
          />
        ))}
        
        {projects.length === 0 && (
          <div className="text-center py-6 text-[var(--crm-text-muted)] text-xs border-2 border-dashed border-[var(--crm-border)] rounded-lg">
            {canDrop ? 'Przeciągnij tutaj' : 'Brak uprawnień'}
          </div>
        )}
      </div>
    </div>
  );
};

export const KanbanBoard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedProject, setDraggedProject] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [updatingProject, setUpdatingProject] = useState(null);
  const [allowedStatuses, setAllowedStatuses] = useState([]);
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [pendingDeliveryDrop, setPendingDeliveryDrop] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchProjects();
    fetchAllowedStatuses();
  }, []);

  useApiCacheRefresh(() => { fetchProjects(); fetchAllowedStatuses(); });

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects`);
      setProjects(response.data);
    } catch (error) {
      toast.error('Błąd ładowania zleceń');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllowedStatuses = async () => {
    try {
      const response = await axios.get(`${API_URL}/user-status-permissions`);
      setAllowedStatuses(response.data.allowed_statuses || []);
    } catch (error) {
      console.error('Error fetching status permissions:', error);
      // Default to all statuses for admin
      if (user?.role === 'admin') {
        setAllowedStatuses(kanbanStatuses);
      }
    }
  };

  const handleDragStart = (e, project) => {
    // Block drag for Duch role
    if (user?.role === 'duch') {
      e.preventDefault();
      toast.error('Funkcja niedostępna w trybie Demo');
      return;
    }
    setDraggedProject(project);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedProject(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    setDragOverColumn(null);
    
    // Block drop for Duch role
    if (user?.role === 'duch') {
      toast.error('Funkcja niedostępna w trybie Demo', {
        description: 'Zmiana statusu wymaga pełnych uprawnień.'
      });
      setDraggedProject(null);
      return;
    }
    
    if (!draggedProject || draggedProject.status === newStatus) {
      setDraggedProject(null);
      return;
    }

    // Check if user can change to this status
    if (!allowedStatuses.includes(newStatus)) {
      toast.error(`Brak uprawnień do zmiany statusu na "${statusConfig[newStatus]?.label || newStatus}"`);
      setDraggedProject(null);
      return;
    }

    // Intercept: if target is 'dostawa', show delivery date modal
    if (newStatus === 'dostawa') {
      setPendingDeliveryDrop({ project: draggedProject, newStatus });
      setDeliveryDate(draggedProject.delivery_date ? new Date(draggedProject.delivery_date) : null);
      setDeliveryModalOpen(true);
      setDraggedProject(null);
      return;
    }

    await performStatusUpdate(draggedProject, newStatus);
    setDraggedProject(null);
  };

  const handleDeliveryConfirm = async () => {
    if (!deliveryDate || !pendingDeliveryDrop) return;
    const dateStr = deliveryDate.toISOString().split('T')[0];
    setDeliveryModalOpen(false);
    await performStatusUpdate(pendingDeliveryDrop.project, pendingDeliveryDrop.newStatus, dateStr);
    setPendingDeliveryDrop(null);
    setDeliveryDate(null);
  };

  const performStatusUpdate = async (project, newStatus, deliveryDateStr = null) => {
    setUpdatingProject(project.id);
    
    // Optimistic update
    setProjects(prev => prev.map(p => 
      p.id === project.id ? { ...p, status: newStatus, ...(deliveryDateStr ? { delivery_date: deliveryDateStr } : {}) } : p
    ));

    try {
      const updateData = {
        nazwa: project.nazwa,
        klient: project.klient,
        adres_inwestycji: project.adres_inwestycji,
        telefon_klienta: project.telefon_klienta,
        email_klienta: project.email_klienta,
        total_offer: project.total_offer,
        costs: project.costs,
        status: newStatus,
        description: project.description,
        owner_id: project.owner_id,
        delivery_date: deliveryDateStr || project.delivery_date || null
      };

      const response = await axios.patch(`${API_URL}/projects/${project.id}`, updateData);
      
      // Update with server response (includes AI analysis and completed_at)
      setProjects(prev => prev.map(p => 
        p.id === project.id ? response.data : p
      ));

      if (newStatus === 'zakonczone') {
        toast.success('Zlecenie zakończone! Analiza AI została wygenerowana.', {
          duration: 5000
        });
      } else if (newStatus === 'dostawa' && deliveryDateStr) {
        toast.success(`Status zmieniony na "Dostawa" — data dostawy: ${new Date(deliveryDateStr).toLocaleDateString('pl-PL')}`);
      } else {
        toast.success(`Status zmieniony na "${statusConfig[newStatus]?.label || newStatus}"`);
      }
    } catch (error) {
      // Revert on error
      fetchProjects();
      const errorMessage = error.response?.data?.detail || 'Błąd aktualizacji statusu';
      toast.error(errorMessage);
    } finally {
      setUpdatingProject(null);
    }
  };

  const getProjectsByStatus = (status) => {
    return projects.filter(p => p.status === status);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-4xl font-manrope font-bold text-[var(--crm-text-primary)] tracking-tight flex items-center gap-3">
              <LayoutGrid className="w-8 h-8 md:w-10 md:h-10 text-[#FF6B35]" />
              Tablica Zleceń
            </h1>
            <p className="text-[var(--crm-text-secondary)] mt-1">Przeciągnij kartę aby zmienić status</p>
          </div>
          
          <div className="flex gap-2">
            <Link to="/archiwum">
              <Button 
                variant="outline"
                className="bg-transparent border-gray-500 text-[var(--crm-text-secondary)] hover:bg-gray-700 hover:text-white"
              >
                <Archive className="w-4 h-4 mr-2" />
                Archiwum
              </Button>
            </Link>
            <Button 
              onClick={() => navigate('/projects')}
              variant="outline"
              className="bg-transparent border-[var(--crm-border)] text-[var(--crm-text-secondary)] hover:bg-[var(--crm-bg-btn-secondary)] hover:text-white"
            >
              Widok listy
            </Button>
          </div>
        </div>

        {/* Info banner */}
        {user?.role === 'montaz' ? (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-300 text-sm font-medium">Widok ograniczony dla roli Montażysta</p>
              <p className="text-amber-400/70 text-xs mt-1">
                Widzisz tylko statusy: Dostawa, W realizacji i Zakończono. 
                Możesz zmieniać projekty tylko między tymi statusami.
              </p>
            </div>
          </div>
        ) : user?.role === 'duch' ? (
          <div className="bg-gray-500/10 border border-gray-500/30 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Lock className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-gray-300 text-sm font-medium">🔒 Tryb Demo - Tylko do przeglądania</p>
              <p className="text-gray-400/70 text-xs mt-1">
                Dane finansowe i kontaktowe są ukryte. Nie możesz zmieniać statusów ani edytować projektów.
                Aby uzyskać pełny dostęp, skontaktuj się z administratorem.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-purple-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-purple-300 text-sm font-medium">Automatyczna analiza AI</p>
              <p className="text-purple-400/70 text-xs mt-1">
                Przeciągnięcie karty do kolumny "Zakończono" automatycznie wygeneruje analizę AI i ustawi datę zakończenia.
                Status "Brak akceptacji" przenosi zlecenie do Archiwum i wyklucza z obliczeń marży.
              </p>
            </div>
          </div>
        )}

        {/* Kanban Board - dla montażysty pokaż tylko statusy 5,6,7 */}
        <div className="flex gap-3 overflow-x-auto pb-4">
          {(user?.role === 'montaz' ? MONTAZ_VISIBLE_STATUSES : kanbanStatuses).map(status => (
            <KanbanColumn
              key={status}
              status={status}
              projects={getProjectsByStatus(status)}
              onDrop={handleDrop}
              onDragOver={(e) => {
                handleDragOver(e);
                setDragOverColumn(status);
              }}
              onCardDragStart={handleDragStart}
              onCardDragEnd={handleDragEnd}
              isDragOver={dragOverColumn === status && draggedProject?.status !== status}
              allowedStatuses={allowedStatuses}
            />
          ))}
        </div>

        {/* Loading overlay for updating project */}
        {updatingProject && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-[var(--crm-bg-main)] rounded-lg p-6 flex items-center gap-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#FF6B35]" />
              <span className="text-[var(--crm-text-primary)]">Aktualizacja statusu...</span>
            </div>
          </div>
        )}

        {/* Delivery Date Modal */}
        <Dialog open={deliveryModalOpen} onOpenChange={(open) => {
          if (!open) {
            setDeliveryModalOpen(false);
            setPendingDeliveryDrop(null);
            setDeliveryDate(null);
          }
        }}>
          <DialogContent className="bg-[var(--crm-bg-main)] border-[var(--crm-border)] text-white sm:max-w-md" data-testid="delivery-date-modal">
            <DialogHeader>
              <DialogTitle className="text-[var(--crm-text-primary)] flex items-center gap-2">
                <Truck className="w-5 h-5 text-purple-400" />
                Planowana data dostawy
              </DialogTitle>
              <DialogDescription className="text-[var(--crm-text-secondary)]">
                Wybierz planowaną datę dostawy na magazyn/do klienta
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center py-2">
              <CalendarComponent
                mode="single"
                selected={deliveryDate}
                onSelect={setDeliveryDate}
                locale={pl}
                className="rounded-md border border-[var(--crm-border)] bg-[var(--crm-bg-panel)]"
                data-testid="delivery-date-picker"
                classNames={{
                  months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                  month: "space-y-4",
                  caption: "flex justify-center pt-1 relative items-center",
                  caption_label: "text-sm font-medium text-[var(--crm-text-primary)]",
                  nav: "space-x-1 flex items-center",
                  nav_button: "h-9 w-9 bg-transparent p-0 opacity-60 hover:opacity-100 border border-[var(--crm-border)] rounded-md inline-flex items-center justify-center text-white hover:bg-[var(--crm-bg-btn-secondary)]",
                  nav_button_previous: "absolute left-1",
                  nav_button_next: "absolute right-1",
                  table: "w-full border-collapse space-y-1",
                  head_row: "flex",
                  head_cell: "text-[var(--crm-text-secondary)] rounded-md w-10 font-normal text-[0.8rem]",
                  row: "flex w-full mt-2",
                  cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-[#FF6B35]/20 [&:has([aria-selected])]:rounded-md",
                  day: "h-10 w-10 p-0 font-normal text-[var(--crm-text-secondary)] hover:bg-[var(--crm-bg-btn-secondary)] hover:text-white rounded-md inline-flex items-center justify-center aria-selected:opacity-100 transition-colors",
                  day_selected: "bg-[#FF6B35] text-white hover:bg-[#FF6B35] hover:text-white focus:bg-[#FF6B35] focus:text-white",
                  day_today: "bg-[var(--crm-bg-btn-secondary)] text-white font-bold",
                  day_outside: "text-slate-600 opacity-50",
                  day_disabled: "text-slate-600 opacity-30",
                  day_hidden: "invisible",
                }}
              />
            </div>
            {deliveryDate && (
              <p className="text-center text-sm text-[var(--crm-text-secondary)]">
                Wybrana data: <span className="text-purple-300 font-semibold">{formatDatePL(deliveryDate)}</span>
              </p>
            )}
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => { setDeliveryModalOpen(false); setPendingDeliveryDrop(null); setDeliveryDate(null); }}
                className="border-[var(--crm-border)] text-[var(--crm-text-secondary)] hover:bg-[var(--crm-bg-btn-secondary)]"
              >
                Anuluj
              </Button>
              <Button
                onClick={handleDeliveryConfirm}
                disabled={!deliveryDate}
                data-testid="delivery-date-confirm"
                className="bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
              >
                <Truck className="w-4 h-4 mr-2" />
                Potwierdź dostawę
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default KanbanBoard;
